import * as React from 'react';
import classnames from '../../utils/classnames.tsx';
import resolveChildren from '../../utils/resolveChildren.tsx';

interface FieldContextValue {
    disabled?: boolean;
    readOnly?: boolean;
}

const FieldContext = React.createContext<FieldContextValue>({});

export interface FieldProps {
    ref?: React.Ref<any>;
    defaultValue?: string;
    value?: string;
    onValueChange?: (value: any) => void;
    // Fired after the user finalized their input (blur, option select, picker close), with the
    // field's final (normalized) value. May fire without an actual value change — consumers
    // must deduplicate. Wrappers that correct values in their blur handlers (e.g.
    // force-selection) intercept this and substitute the corrected value; wrappers that carry
    // extra commit data (e.g. lookups with _lookup_id/_lookupEntity) attach it as
    // updatePayload. Base fields themselves always fire it with the value only.
    onCommit?: (value: any, updatePayload?: any) => void;
    controls?: React.ReactNode;
    inputRef?: React.Ref<any>;
}

const Field = ({ className, readOnly, disabled, ...props }: any) => (
    <FieldContext.Provider value={{ readOnly, disabled }}>
        <div
            className={classnames(
                'flex w-full shadow-inner-sm border border-solid outline-2 outline-transparent border-line-default rounded bg-surface-weak text-sm text-content-strong transition-colors duration-200',
                !readOnly &&
                    'hover:border-line-hover focus-within:border-line-focus focus-within:outline-line-weak open-within:border-line-focus open-within:outline-line-weak',
                disabled && 'bg-surface-strong shadow-none text-content-strong',
                className
            )}
            {...props}
        />
    </FieldContext.Provider>
);

const FieldInput = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div className={classnames('flex flex-1 rounded overflow-hidden', className)} {...props} />
);

const FieldControls = ({ className, children, ...props }: any) => {
    const resolvedChildren = resolveChildren(children);
    const childrenWithSeparators = resolvedChildren
        .flatMap((x, i) => [<span key={i} className="border-r border-solid border-line-weak h-4" />, x])
        .slice(1);

    return (
        <div
            className={classnames('flex-none box-content top-0 right-0 flex items-center p-0.5 gap-0.5', className)}
            {...props}
        >
            {childrenWithSeparators}
        </div>
    );
};

const FieldControlButton = ({
    className,
    active,
    ...props
}: React.ComponentPropsWithoutRef<'button'> & { active?: boolean }) => {
    const { disabled } = React.useContext(FieldContext);
    return (
        <button
            type="button"
            className={classnames(
                'flex rounded justify-center items-center box-content min-w-7 min-h-7 text-sm text-content-weak transition-all duration-200 focus:opacity-100 cursor-pointer', // opacity-50 hover:opacity-100
                !disabled && 'hover:bg-surface-strong hover:text-content-medium',
                disabled && '!text-content-weak',
                active &&
                    'bg-surface-brand-medium-default text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active',
                className
            )}
            tabIndex={-1}
            {...props}
        />
    );
};

export default Object.assign(Field, {
    Input: FieldInput,
    Controls: FieldControls,
    ControlButton: FieldControlButton,
});
